import { useMemo, useState } from 'preact/hooks';
import { rpx, RpxResponse } from 'client/lib/rpx-client';
import { defRoute, LoadedProps } from '@components/router';
import { FixedContent, FixedPage } from '@course/components/fixed-page';
import { AdminNav, AdminTabHeader, AdminTabWrapper } from '@course/components/admin-nav';
import { BtnPrimary } from '@components/buttons';
import { IcoExternalLink, IcoPlus, IcoSearch } from '@components/icons';

type Campaign = RpxResponse<typeof rpx.campaigns.listCampaigns>[0];
type Stage = Campaign['stage'];
type CampaignPrice = Campaign['prices'][0];

async function load() {
  return rpx.campaigns.listCampaigns();
}

export const route = defRoute({
  load,
  Page,
  authLevel: 'guide',
  hideChat: true,
});

const TABS: Array<{ key: Stage; label: string; dot: string }> = [
  { key: 'selling', label: 'Selling', dot: 'bg-emerald-500' },
  { key: 'running', label: 'Running', dot: 'bg-violet-500' },
  { key: 'draft', label: 'Drafts', dot: 'bg-gray-400' },
];

function formatDollars(cents: number): string {
  const dollars = cents / 100;
  if (dollars >= 1000) {
    return `$${dollars.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
  }
  if (Number.isInteger(dollars)) {
    return `$${dollars}`;
  }
  return `$${dollars.toFixed(2)}`;
}

function formatPriceLabel(price: CampaignPrice): string {
  const amount = formatDollars(price.priceInCents);
  if (price.paymentType === 'subscription' && price.interval) {
    const intervalShort =
      price.interval === 'month' ? 'mo' : price.interval === 'year' ? 'yr' : price.interval;
    return `${amount}/${intervalShort}`;
  }
  if (price.paymentType === 'paymentplan') {
    return `${amount} PP`;
  }
  return amount;
}

function summarizePrices(prices: CampaignPrice[]): { primary: string; secondary?: string } {
  if (prices.length === 0) {
    return { primary: 'No price' };
  }
  const labels = prices.map(formatPriceLabel);
  return { primary: labels.join(' · ') };
}

function summarizeUpsell(upsell: NonNullable<Campaign['upsell']>): string {
  const amount = formatDollars(upsell.priceInCents);
  let label: string;
  if (upsell.paymentType === 'subscription' && upsell.interval) {
    const intervalShort =
      upsell.interval === 'month' ? 'mo' : upsell.interval === 'year' ? 'yr' : upsell.interval;
    label = `${amount}/${intervalShort}`;
  } else {
    label = amount;
  }
  if (upsell.freeTrialDays && upsell.freeTrialDays > 0) {
    return `+ ${upsell.courseTitle} ${label} · ${upsell.freeTrialDays}-day trial`;
  }
  return `+ ${upsell.courseTitle} ${label}`;
}

function programNameFromTitle(title: string): string {
  const splitters = [' — ', ' – ', ' - ', ' ('];
  for (const sep of splitters) {
    const idx = title.indexOf(sep);
    if (idx > 0) {
      return title.slice(0, idx).trim();
    }
  }
  return title.trim();
}

function modalityBreadcrumb(campaign: Campaign): string {
  const program = programNameFromTitle(campaign.courseTitle);
  switch (campaign.modality) {
    case 'cohort':
      return `${program} cohort`;
    case 'subscription':
      return `${program} subscription`;
    case 'event':
      return `${program} event`;
    case 'evergreen':
      return `${program} evergreen`;
  }
}

function formatCohortDate(dateStr: string): string {
  const [y, m, d] = dateStr.split('-').map(Number);
  const date = new Date(Date.UTC(y, m - 1, d));
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

function relativeDays(dateStr: string): { label: string; isPast: boolean } {
  const today = new Date();
  today.setUTCHours(0, 0, 0, 0);
  const [y, m, d] = dateStr.split('-').map(Number);
  const target = new Date(Date.UTC(y, m - 1, d));
  const diffDays = Math.round((target.getTime() - today.getTime()) / 86_400_000);
  if (diffDays === 0) {
    return { label: 'today', isPast: false };
  }
  if (diffDays > 0) {
    return { label: `in ${diffDays} ${diffDays === 1 ? 'day' : 'days'}`, isPast: false };
  }
  const past = -diffDays;
  return { label: `${past} ${past === 1 ? 'day' : 'days'} ago`, isPast: true };
}

function relativeFromTimestamp(iso: string): string {
  const target = new Date(iso);
  const now = new Date();
  const diffSec = Math.round((now.getTime() - target.getTime()) / 1000);
  if (diffSec < 60) {
    return 'just now';
  }
  if (diffSec < 3600) {
    const m = Math.round(diffSec / 60);
    return `${m}m ago`;
  }
  if (diffSec < 86400) {
    const h = Math.round(diffSec / 3600);
    return `${h}h ago`;
  }
  const days = Math.round(diffSec / 86400);
  if (days < 30) {
    return `${days}d ago`;
  }
  const months = Math.round(days / 30);
  return `${months}mo ago`;
}

type HealthIssue = {
  key: 'welcome' | 'ontraportTags';
  severity: 'warn' | 'error';
  shortLabel: string;
  alertCopy: string;
  alertCta: string;
};

function deriveHealthIssues(campaign: Campaign): HealthIssue[] {
  const issues: HealthIssue[] = [];
  if (!campaign.welcomeConfigured) {
    issues.push({
      key: 'welcome',
      severity: 'error',
      shortLabel: 'Welcome page unset',
      alertCopy: 'Buyers see the generic Mirasee welcome instead of campaign-specific content.',
      alertCta: 'Set welcome page →',
    });
  }
  if (!campaign.ontraportTagsConfigured) {
    issues.push({
      key: 'ontraportTags',
      severity: 'warn',
      shortLabel: 'OP tags missing',
      alertCopy: "No Ontraport tags configured — purchases won't trigger OP automations.",
      alertCta: 'Configure tags →',
    });
  }
  return issues;
}

function CheckIcon() {
  return (
    <svg class="size-3" fill="none" stroke="currentColor" stroke-width="3" viewBox="0 0 24 24">
      <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
    </svg>
  );
}

function WarningIcon() {
  return (
    <svg class="size-3" fill="currentColor" viewBox="0 0 20 20">
      <path
        fill-rule="evenodd"
        clip-rule="evenodd"
        d="M8.485 3.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 3.495zM10 6a1 1 0 011 1v3a1 1 0 11-2 0V7a1 1 0 011-1zm0 8a1 1 0 100-2 1 1 0 000 2z"
      />
    </svg>
  );
}

function HealthChip({ issues }: { issues: HealthIssue[] }) {
  if (issues.length === 0) {
    return (
      <span class="text-[11px] font-medium text-emerald-700 inline-flex items-center gap-1 whitespace-nowrap">
        <CheckIcon />
        All set
      </span>
    );
  }
  const hasError = issues.some((i) => i.severity === 'error');
  const colorClass = hasError ? 'text-red-700' : 'text-amber-700';
  const label = issues.length === 1 ? issues[0].shortLabel : `${issues.length} issues`;
  return (
    <span
      class={`text-[11px] font-medium ${colorClass} inline-flex items-center gap-1 whitespace-nowrap`}
    >
      <WarningIcon />
      {label}
    </span>
  );
}

function AlertStrip({ issues, editHref }: { issues: HealthIssue[]; editHref: string }) {
  if (issues.length === 0) {
    return null;
  }
  const hasError = issues.some((i) => i.severity === 'error');
  const colorClass = hasError ? 'border-red-100 text-red-700' : 'border-amber-100 text-amber-700';
  if (issues.length === 1) {
    const issue = issues[0];
    return (
      <div class={`relative mt-3 pt-3 border-t text-[12px] ${colorClass}`}>
        {issue.alertCopy}{' '}
        <a href={editHref} class="font-medium underline hover:no-underline pointer-events-auto">
          {issue.alertCta}
        </a>
      </div>
    );
  }
  return (
    <div class={`relative mt-3 pt-3 border-t text-[12px] ${colorClass}`}>
      <ul class="flex flex-col gap-1 mb-2">
        {issues.map((issue) => (
          <li key={issue.key} class="flex gap-2">
            <span aria-hidden="true">·</span>
            <span>{issue.alertCopy}</span>
          </li>
        ))}
      </ul>
      <a href={editHref} class="font-medium underline hover:no-underline pointer-events-auto">
        Open campaign →
      </a>
    </div>
  );
}

function StageChip({ stage }: { stage: Stage }) {
  if (stage === 'selling') {
    return (
      <span class="text-[11px] font-medium px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800 leading-none flex-shrink-0">
        Selling
      </span>
    );
  }
  if (stage === 'running') {
    return (
      <span class="text-[11px] font-medium px-1.5 py-0.5 rounded bg-violet-100 text-violet-800 leading-none flex-shrink-0">
        Running
      </span>
    );
  }
  return (
    <span class="text-[11px] font-medium px-1.5 py-0.5 rounded bg-gray-100 text-gray-700 leading-none flex-shrink-0">
      Draft
    </span>
  );
}

function CampaignRowCard({ campaign }: { campaign: Campaign }) {
  const issues = deriveHealthIssues(campaign);
  const editHref = `/admin/campaigns/${campaign.courseId}`;
  const publicHref = `/c/${campaign.campaignSlug}`;

  const cohortLabel = campaign.deferredEnrollmentDate
    ? formatCohortDate(campaign.deferredEnrollmentDate)
    : campaign.modality === 'subscription'
      ? 'Rolling'
      : 'Immediate';

  const cohortSubLabel = campaign.deferredEnrollmentDate
    ? relativeDays(campaign.deferredEnrollmentDate).label
    : 'no cohort date';

  const hasEnrollments = campaign.enrollmentCount > 0;
  const enrollmentLabel =
    campaign.modality === 'subscription' && hasEnrollments
      ? `${campaign.enrollmentCount} members`
      : `${campaign.enrollmentCount} enrollment${campaign.enrollmentCount === 1 ? '' : 's'}`;

  const revenueLabel =
    campaign.revenueInCents > 0
      ? `${formatDollars(campaign.revenueInCents)} collected`
      : campaign.lastSaleAt
        ? `last sale ${relativeFromTimestamp(campaign.lastSaleAt)}`
        : 'no sales yet';

  const prices = summarizePrices(campaign.prices);
  const upsellLabel = campaign.upsell ? summarizeUpsell(campaign.upsell) : undefined;

  const hasError = issues.some((i) => i.severity === 'error');
  const hasWarn = issues.some((i) => i.severity === 'warn');
  const borderClass = hasError
    ? 'border-red-200'
    : hasWarn
      ? 'border-amber-200'
      : 'border-gray-200 hover:border-gray-300';

  return (
    <article
      class={`relative bg-white rounded-xl border ${borderClass} px-5 py-4 transition group`}
    >
      <a
        href={editHref}
        class="absolute inset-0 rounded-xl"
        aria-label={`Edit ${campaign.courseTitle}`}
      />
      <div class="relative grid grid-cols-12 gap-5 items-center pointer-events-none">
        <div class="col-span-4 min-w-0">
          <div class="flex items-center gap-2">
            <span class="text-[15px] font-semibold text-gray-900 truncate">
              {campaign.courseTitle}
            </span>
            <StageChip stage={campaign.stage} />
          </div>
          <div class="flex items-center gap-2 mt-1 text-[12px] text-gray-500 min-w-0">
            <span class="flex-shrink-0">{modalityBreadcrumb(campaign)}</span>
            <span class="text-gray-300">·</span>
            <span class="font-mono text-[11px] text-gray-400 truncate">
              /c/{campaign.campaignSlug}
            </span>
          </div>
        </div>

        <div class="col-span-2 min-w-0">
          <div class="text-[14px] font-medium text-gray-900 tabular-nums leading-tight truncate">
            {cohortLabel}
          </div>
          <div class="text-[12px] text-gray-500 mt-0.5 truncate">{cohortSubLabel}</div>
        </div>

        <div class="col-span-2 min-w-0">
          <div
            class={`text-[14px] font-medium tabular-nums leading-tight truncate ${
              hasEnrollments ? 'text-gray-900' : 'text-gray-400'
            }`}
          >
            {enrollmentLabel}
          </div>
          <div
            class={`text-[12px] mt-0.5 tabular-nums truncate ${
              hasEnrollments || campaign.lastSaleAt ? 'text-gray-500' : 'text-gray-400'
            }`}
          >
            {revenueLabel}
          </div>
        </div>

        <div class="col-span-2 min-w-0">
          <div class="text-[13px] text-gray-700 tabular-nums leading-tight truncate">
            {prices.primary}
          </div>
          <div
            class={`text-[12px] mt-0.5 truncate ${
              upsellLabel ? 'text-gray-500' : 'text-gray-400 italic'
            }`}
          >
            {upsellLabel ?? 'no upsell'}
          </div>
        </div>

        <div class="col-span-2 flex flex-col items-end gap-1.5">
          <HealthChip issues={issues} />
          <div class="pointer-events-auto flex items-center gap-0.5 -mr-1.5 opacity-60 group-hover:opacity-100 transition">
            <a
              href={editHref}
              class="text-[12px] px-2 py-1 rounded text-gray-600 hover:bg-gray-100"
            >
              Edit
            </a>
            <a
              href={publicHref}
              target="_blank"
              rel="noopener noreferrer"
              class="text-[12px] px-2 py-1 rounded text-gray-600 hover:bg-gray-100 inline-flex items-center"
              aria-label="Open public page"
              onClick={(e) => e.stopPropagation()}
            >
              <IcoExternalLink class="size-3.5" />
            </a>
          </div>
        </div>
      </div>

      <AlertStrip issues={issues} editHref={editHref} />
    </article>
  );
}

function DraftRowCard({ campaign }: { campaign: Campaign }) {
  const editHref = `/admin/campaigns/${campaign.courseId}`;
  const prices = summarizePrices(campaign.prices);

  return (
    <article class="relative bg-white rounded-xl border border-gray-200 px-5 py-4 hover:border-gray-300 transition group">
      <a
        href={editHref}
        class="absolute inset-0 rounded-xl"
        aria-label={`Edit ${campaign.courseTitle}`}
      />
      <div class="relative grid grid-cols-12 gap-5 items-center pointer-events-none">
        <div class="col-span-4 min-w-0">
          <div class="flex items-center gap-2">
            <span class="text-[15px] font-semibold text-gray-900 truncate">
              {campaign.courseTitle}
            </span>
            <StageChip stage="draft" />
          </div>
          <div class="flex items-center gap-2 mt-1 text-[12px] text-gray-500 min-w-0">
            <span class="font-mono text-[11px] text-gray-400 truncate">
              /c/{campaign.campaignSlug}
            </span>
          </div>
        </div>
        <div class="col-span-4 min-w-0" />
        <div class="col-span-2 min-w-0">
          <div class="text-[13px] text-gray-700 tabular-nums leading-tight truncate">
            {prices.primary}
          </div>
        </div>
        <div class="col-span-2 pointer-events-auto flex items-center justify-end gap-0.5 -mr-1.5 opacity-60 group-hover:opacity-100 transition">
          <a href={editHref} class="text-[12px] px-2 py-1 rounded text-gray-600 hover:bg-gray-100">
            Edit
          </a>
        </div>
      </div>
    </article>
  );
}

function EmptyTab({ stage }: { stage: Stage }) {
  const messages: Record<Stage, string> = {
    selling: 'No campaigns are accepting enrollments right now.',
    running: 'No cohorts are currently underway.',
    draft: 'No drafts in progress.',
  };
  return <div class="text-center py-12 text-sm text-gray-500">{messages[stage]}</div>;
}

function EmptyState() {
  return (
    <div class="text-center py-16">
      <h3 class="text-lg font-semibold text-gray-900 mb-2">No campaigns yet</h3>
      <p class="text-gray-500 mb-6">Create a checkout page for your next event or offer.</p>
      <BtnPrimary href="/admin/campaigns/new">
        <IcoPlus class="size-4" />
        Create Campaign
      </BtnPrimary>
    </div>
  );
}

const PANEL_DESCRIPTIONS: Record<Stage, string> = {
  selling: 'Currently accepting enrollments · sorted by cohort start',
  running: 'Cohort underway · sales window closed',
  draft: 'Not yet live · setup in progress',
};

function compareForStage(a: Campaign, b: Campaign, stage: Stage): number {
  if (stage === 'selling') {
    if (a.deferredEnrollmentDate && b.deferredEnrollmentDate) {
      return a.deferredEnrollmentDate.localeCompare(b.deferredEnrollmentDate);
    }
    if (a.deferredEnrollmentDate) {
      return -1;
    }
    if (b.deferredEnrollmentDate) {
      return 1;
    }
    return 0;
  }
  if (stage === 'running') {
    if (a.deferredEnrollmentDate && b.deferredEnrollmentDate) {
      return b.deferredEnrollmentDate.localeCompare(a.deferredEnrollmentDate);
    }
    return 0;
  }
  return 0;
}

function Page(props: LoadedProps<typeof load>) {
  const campaigns = props.state;
  const [activeStage, setActiveStage] = useState<Stage>('selling');
  const [search, setSearch] = useState('');
  const [program, setProgram] = useState<string>('');

  const programs = useMemo(() => {
    const set = new Set<string>();
    for (const c of campaigns) {
      set.add(programNameFromTitle(c.courseTitle));
    }
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [campaigns]);

  const counts = useMemo(() => {
    const result: Record<Stage, number> = { selling: 0, running: 0, draft: 0 };
    for (const c of campaigns) {
      result[c.stage] += 1;
    }
    return result;
  }, [campaigns]);

  const filteredByStage = useMemo(() => {
    const q = search.trim().toLowerCase();
    return campaigns
      .filter((c) => c.stage === activeStage)
      .filter((c) => {
        if (program && programNameFromTitle(c.courseTitle) !== program) {
          return false;
        }
        if (q) {
          return (
            c.courseTitle.toLowerCase().includes(q) || c.campaignSlug.toLowerCase().includes(q)
          );
        }
        return true;
      })
      .sort((a, b) => compareForStage(a, b, activeStage));
  }, [campaigns, activeStage, search, program]);

  if (campaigns.length === 0) {
    return (
      <FixedPage>
        <FixedContent class="bg-white">
          <AdminNav currentPage="campaigns" pageTitle="Campaigns" />
          <AdminTabWrapper>
            <AdminTabHeader title="Campaigns" />
            <EmptyState />
          </AdminTabWrapper>
        </FixedContent>
      </FixedPage>
    );
  }

  return (
    <FixedPage>
      <FixedContent class="bg-white">
        <AdminNav currentPage="campaigns" pageTitle="Campaigns" />
        <AdminTabWrapper>
          <AdminTabHeader title="Campaigns">
            <BtnPrimary href="/admin/campaigns/new">
              <IcoPlus class="size-4" />
              New Campaign
            </BtnPrimary>
          </AdminTabHeader>

          {/* Search + filter row */}
          <div class="flex items-center gap-3 mb-4">
            <div class="relative flex-1 max-w-md">
              <IcoSearch class="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search by name or slug…"
                value={search}
                onInput={(e) => setSearch((e.target as HTMLInputElement).value)}
                class="w-full pl-10 pr-3 py-2 text-sm bg-white border border-gray-200 rounded-lg focus:outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100"
              />
            </div>
            {programs.length > 1 && (
              <select
                value={program}
                onChange={(e) => setProgram((e.target as HTMLSelectElement).value)}
                class="text-sm bg-white border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:border-indigo-400"
              >
                <option value="">All programs</option>
                {programs.map((p) => (
                  <option key={p} value={p}>
                    {p}
                  </option>
                ))}
              </select>
            )}
          </div>

          {/* Tabs */}
          <div class="border-b border-gray-200 mb-6 flex items-center gap-6">
            {TABS.map((tab) => {
              const isActive = tab.key === activeStage;
              return (
                <button
                  key={tab.key}
                  type="button"
                  onClick={() => setActiveStage(tab.key)}
                  class={`flex items-center gap-2 py-3 -mb-px border-b-2 text-sm font-medium ${
                    isActive
                      ? 'border-indigo-600 text-gray-900'
                      : 'border-transparent text-gray-500 hover:text-gray-900'
                  }`}
                >
                  <span class={`size-2 rounded-full ${tab.dot}`} />
                  {tab.label}
                  <span class="text-xs font-semibold px-1.5 py-0.5 rounded bg-gray-100 text-gray-600">
                    {counts[tab.key]}
                  </span>
                </button>
              );
            })}
          </div>

          {/* Active panel */}
          <p class="text-xs text-gray-500 mb-3">{PANEL_DESCRIPTIONS[activeStage]}</p>

          {filteredByStage.length === 0 ? (
            <EmptyTab stage={activeStage} />
          ) : (
            <div class="flex flex-col gap-2">
              {filteredByStage.map((c) =>
                c.stage === 'draft' ? (
                  <DraftRowCard key={c.courseId} campaign={c} />
                ) : (
                  <CampaignRowCard key={c.courseId} campaign={c} />
                ),
              )}
            </div>
          )}
        </AdminTabWrapper>
      </FixedContent>
    </FixedPage>
  );
}
